import React, { useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import { selectActiveGame, selectActivePokemon, selectHand, selectOptions } from '../ActiveGame/selectors';
import { setContextMenu } from '../ActiveGame/actions';
import { HAND } from '../ActiveGame/contextMenuTypes';
import ContextMenu from '../ContextMenu';
import CardDetail from '../ContextMenu/CardDetail';
import HandMenu from './HandMenu';
import DraggableHandCard from './DraggableHandCard';
import { handCardAction } from './handCardAction';
import { Label, Tray } from './styled';
import Anchor from '../Motion/Anchor';
import { anchors } from '../Motion/anchorNames';
import DropTarget from '../Drag/DropTarget';
import { useDragging } from '../Drag/dropTargets';
import { DECK_TO_HAND } from '../Deck/constants';
import { draw } from '../Deck/actions';

/**
 * Your hand, always on screen. Tapping a card opens what you can do with it; dragging it up plays it on the board.
 * Dragging your deck onto it draws your turn's card.
 */
const Hand = ({ userId }) => {
  const dispatch = useDispatch();
  const hand = useSelector((state) => selectHand(state, { userId }));
  const [chosenPivotId, choose] = useState(null);
  const game = useSelector(selectActiveGame);
  const activePokemon = useSelector((state) => selectActivePokemon(state, { userId }));
  const options = useSelector(selectOptions);
  const dragging = useDragging();
  const drawingHere = !!dragging && dragging.kind === DECK_TO_HAND;
  const situation = useMemo(
    () => ({ settingUp: !game.get('active_player_id'), hasActive: !!activePokemon, options }),
    [game, activePokemon, options],
  );

  if (!hand) {
    return null;
  }

  const cards = hand.get('cards').sortBy(card => card.get('order'));
  const chosen = cards.find(card => card.get('pivot_id') === chosenPivotId);

  return (
    <>
      <Label>Your hand · {cards.size}</Label>
      {drawingHere && <DropTarget name={anchors.hand(userId)} commit={() => dispatch(draw())} />}
      <Anchor name={anchors.hand(userId)}>
        <Tray aria-label="Your hand" $lit={drawingHere}>
          {cards.map(card => (
            <Anchor key={card.get('pivot_id')} name={anchors.handCard(userId, card.get('pivot_id'))}>
              <DraggableHandCard
                card={card}
                place={anchors.handCard(userId, card.get('pivot_id'))}
                action={handCardAction(card, situation)}
                onTap={() => {
                  choose(card.get('pivot_id'));
                  dispatch(setContextMenu(HAND, userId));
                }}
              />
          </Anchor>
        ))}
      </Tray>
      </Anchor>
      {chosen && <ContextMenu menu={HAND} userId={userId} title={chosen.get('name')}>
        <CardDetail card={chosen}>
          <HandMenu card={chosen} userId={userId} />
        </CardDetail>
      </ContextMenu>}
    </>
  );
};

Hand.propTypes = {
  userId: PropTypes.number.isRequired,
};

export default Hand;
